import React, { useState, useEffect, useRef, useContext } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import pagosService from '../axios_services/finanzaspago.service';
import ModalFinanzasPago from '../components/Modal/ModalFinanzasPago';
import Loader from '../components/Loader/Loader';
import { ButtonSearch, ButtonUpdate, ButtonDelete, ButtonInsert, ButtonExcel } from '../components/Buttons/Buttons';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─── Catálogos (etiquetas para mostrar en tabla) ──────────────────────────────
const LABEL_TIPO_COMPROBANTE = { 1: 'Factura', 2: 'Boleta', 3: 'Rec. Honorarios', 4: 'Nota Crédito', 5: 'Liquidación' };
const LABEL_TIPO_BIEN_SERVICIO = { 1: 'Servicio', 2: 'Producto' };
const LABEL_ESTADO_PROCESO = {
    0: 'Solicitado', 1: 'En Revisión', 2: 'Aprobado', 3: 'Rechazado',
    4: 'En Proceso', 5: 'Pago Parcial', 6: 'Pagado Total', 7: 'Anulado'
};
const LABEL_ESTADO_PAGO = { 0: 'Pendiente', 1: 'Pago Parcial', 2: 'Pagado' };
const LABEL_NIVEL_APROBACION = {
    1: 'Tesorería', 2: 'Jef. Finanzas', 3: 'Ger. Administración', 4: 'Ger. General'
};

const COLOR_ESTADO_PAGO = {
    0: { background: '#fff3cd', color: '#856404' },
    1: { background: '#cce5ff', color: '#004085' },
    2: { background: '#d4edda', color: '#155724' },
};

const COLOR_ESTADO_PROCESO = {
    0: { background: '#e2e3e5', color: '#383d41' },
    1: { background: '#cce5ff', color: '#004085' },
    2: { background: '#d4edda', color: '#155724' },
    3: { background: '#f8d7da', color: '#721c24' },
    4: { background: '#d1ecf1', color: '#0c5460' },
    5: { background: '#fff3cd', color: '#856404' },
    6: { background: '#d4edda', color: '#155724' },
    7: { background: '#f8d7da', color: '#721c24' },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${day}/${m}/${y}`;
};

const formatMonto = (v) =>
    v !== null && v !== undefined && v !== ''
        ? `S/ ${parseFloat(v).toFixed(2)}`
        : '—';

// ─── Component ────────────────────────────────────────────────────────────────
const FinanzasPago = () => {
    const [state] = useContext(AppContext);

    const [pagos, setPagos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPago, setSelectedPago] = useState(null);

    // Filtros
    const [periodoFecha, setPeriodoFecha] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [empresaId, setEmpresaId] = useState('');
    const [estadoPago, setEstadoPago] = useState('');
    const [estadoProceso, setEstadoProceso] = useState('');
    const [nivelAprobacion, setNivelAprobacion] = useState('');

    const initialFetchDone = useRef(false);

    useEffect(() => {
        if (!initialFetchDone.current) {
            fetchPagos();
            initialFetchDone.current = true;
        }
    }, []);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchPagos = async (filters = {}) => {
        setIsLoading(true);
        try {
            const currentPerfilId = Number(state.user?.perfil_id || state.perfil_id);
            const params = {
                perfil_id: currentPerfilId,
                periodo_fecha: filters.periodo_fecha ?? periodoFecha,
                ...(filters.empresa_id !== undefined ? { empresa_id: filters.empresa_id } : empresaId ? { empresa_id: empresaId } : {}),
                ...(filters.estado_pago !== undefined ? { estado_pago: filters.estado_pago } : estadoPago !== '' ? { estado_pago: estadoPago } : {}),
                ...(filters.estado_proceso !== undefined ? { estado_proceso: filters.estado_proceso } : estadoProceso !== '' ? { estado_proceso: estadoProceso } : {}),
                ...(filters.nivel_aprobacion !== undefined ? { nivel_aprobacion: filters.nivel_aprobacion } : nivelAprobacion !== '' ? { nivel_aprobacion: nivelAprobacion } : {}),
            };
            const res = await pagosService.getPagos(params);
            setPagos(res.data ?? []);
        } catch (error) {
            console.error('Error al obtener pagos:', error);
            alert('Error al obtener los pagos.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchClick = () => {
        fetchPagos({
            periodo_fecha: periodoFecha,
            empresa_id: empresaId !== '' ? empresaId : undefined,
            estado_pago: estadoPago !== '' ? estadoPago : undefined,
            estado_proceso: estadoProceso !== '' ? estadoProceso : undefined,
            nivel_aprobacion: nivelAprobacion !== '' ? nivelAprobacion : undefined,
        });
    };

    // ── CRUD ───────────────────────────────────────────────────────────────────
    const handleCreateClick = () => {
        setSelectedPago(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (pago) => {
        setSelectedPago(pago);
        setIsModalOpen(true);
    };

    const handleDeleteClick = async (pago_id) => {
        if (!window.confirm('¿Está seguro de eliminar este pago?')) return;
        setIsLoading(true);
        try {
            const currentPerfilId = Number(state.user?.perfil_id || state.perfil_id);
            await pagosService.deletePago({ pago_id, perfil_id: currentPerfilId });
            await fetchPagos();
            alert('Pago eliminado correctamente.');
        } catch (error) {
            console.error('Error al eliminar pago:', error);
            alert('Error al eliminar el pago.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (payload, isEditing) => {
        if (isEditing) {
            await pagosService.updatePago(payload);
        } else {
            await pagosService.insertPago(payload);
        }
        await fetchPagos();
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPago(null);
    };

    // ── Totales ────────────────────────────────────────────────────────────────
    const totalMonto = pagos.reduce((s, p) => s + (parseFloat(p.monto_total) || 0), 0);
    const totalPendiente = pagos.filter(p => p.estado_pago === 0).reduce((s, p) => s + (parseFloat(p.monto_total) || 0), 0);
    const totalPagado = pagos.filter(p => p.estado_pago === 2).reduce((s, p) => s + (parseFloat(p.monto_total) || 0), 0);

    // ── Exportar Excel ─────────────────────────────────────────────────────────
    const exportarExcel = async () => {
        if (pagos.length === 0) { alert('No hay datos para exportar.'); return; }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Pagos');

        worksheet.columns = [
            { header: 'N° PAGO', key: 'numero_pago', width: 18 },
            { header: 'PROVEEDOR', key: 'proveedor', width: 30 },
            { header: 'FECHA SOLICITUD', key: 'fecha_solicitud', width: 16 },
            { header: 'COMPROBANTE', key: 'tipo_comprobante', width: 18 },
            { header: 'SERIE', key: 'serie_comprobante', width: 10 },
            { header: 'NÚMERO', key: 'numero_comprobante', width: 15 },
            { header: 'TIPO', key: 'tipo_bien_servicio', width: 12 },
            { header: 'MONTO TOTAL', key: 'monto_total', width: 14 },
            { header: 'NIVEL APROBACIÓN', key: 'nivel_aprobacion', width: 20 },
            { header: 'PLAZO (DÍAS)', key: 'plazo_dias', width: 14 },
            { header: 'ESTADO PROCESO', key: 'estado_proceso', width: 18 },
            { header: 'ESTADO PAGO', key: 'estado_pago', width: 15 },
            { header: 'FECHA PAGO', key: 'fecha_pago', width: 14 },
            { header: 'N° OPERACIÓN', key: 'numero_operacion', width: 18 },
            { header: 'OBSERVACIÓN', key: 'observacion', width: 30 },
        ];

        pagos.forEach(p => {
            worksheet.addRow({
                numero_pago: p.numero_pago ?? '',
                proveedor: p.proveedor ?? '',
                fecha_solicitud: formatDate(p.fecha_solicitud),
                tipo_comprobante: LABEL_TIPO_COMPROBANTE[p.tipo_comprobante] ?? p.tipo_comprobante ?? '',
                serie_comprobante: p.serie_comprobante ?? '',
                numero_comprobante: p.numero_comprobante ?? '',
                tipo_bien_servicio: LABEL_TIPO_BIEN_SERVICIO[p.tipo_bien_servicio] ?? '',
                monto_total: parseFloat(p.monto_total) || 0,
                nivel_aprobacion: LABEL_NIVEL_APROBACION[p.nivel_aprobacion] ?? '',
                plazo_dias: p.plazo_dias ?? '',
                estado_proceso: LABEL_ESTADO_PROCESO[p.estado_proceso] ?? '',
                estado_pago: LABEL_ESTADO_PAGO[p.estado_pago] ?? '',
                fecha_pago: formatDate(p.fecha_pago),
                numero_operacion: p.numero_operacion ?? '',
                observacion: p.observacion ?? '',
            });
        });

        // Estilos encabezado
        const headerRow = worksheet.getRow(1);
        headerRow.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
        headerRow.height = 20;
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F497D' } };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FF000000' } },
                left: { style: 'thin', color: { argb: 'FF000000' } },
                bottom: { style: 'thin', color: { argb: 'FF000000' } },
                right: { style: 'thin', color: { argb: 'FF000000' } },
            };
        });

        // Estilos filas
        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            if (rowNumber % 2 === 0) {
                row.eachCell(cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
                });
            }
            row.eachCell((cell, colNumber) => {
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } },
                };
                const colKey = worksheet.columns[colNumber - 1]?.key;
                if (colKey === 'monto_total') {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '"S/ "#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `PagosMec${periodoFecha}.xlsx`);
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    if (isLoading) return <Loader />;

    return (
        <div className="caja-flujo-container">
            <div className="flex items-center justify-between mb-4">
                <h1>FINANZAS — PAGOS</h1>
                <ButtonExcel onClick={exportarExcel} disabled={pagos.length === 0} />
            </div>

            {/* Filtros */}
            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="periodo_fecha">Período:</label>
                    <input
                        id="periodo_fecha"
                        type="month"
                        value={periodoFecha}
                        onChange={e => setPeriodoFecha(e.target.value)}
                    />
                </div>

                <div className="campo-fecha">
                    <label htmlFor="empresa_id">Empresa ID:</label>
                    <input
                        id="empresa_id"
                        type="number"
                        value={empresaId}
                        onChange={e => setEmpresaId(e.target.value)}
                        placeholder="Todas"
                    />
                </div>

                <div className="campo-fecha">
                    <label htmlFor="estado_pago">Estado Pago:</label>
                    <select
                        id="estado_pago"
                        value={estadoPago}
                        onChange={e => setEstadoPago(e.target.value)}
                    >
                        <option value="">Todos</option>
                        <option value="0">Pendiente</option>
                        <option value="1">Pago Parcial</option>
                        <option value="2">Pagado</option>
                    </select>
                </div>

                <div className="campo-fecha">
                    <label htmlFor="estado_proceso">Estado Proceso:</label>
                    <select
                        id="estado_proceso"
                        value={estadoProceso}
                        onChange={e => setEstadoProceso(e.target.value)}
                    >
                        <option value="">Todos</option>
                        <option value="0">Solicitado</option>
                        <option value="1">En Revisión</option>
                        <option value="2">Aprobado</option>
                        <option value="3">Rechazado</option>
                        <option value="4">En Proceso</option>
                        <option value="5">Pago Parcial</option>
                        <option value="6">Pagado Total</option>
                        <option value="7">Anulado</option>
                    </select>
                </div>

                <div className="campo-fecha">
                    <label htmlFor="nivel_aprobacion">Nivel Aprobación:</label>
                    <select
                        id="nivel_aprobacion"
                        value={nivelAprobacion}
                        onChange={e => setNivelAprobacion(e.target.value)}
                    >
                        <option value="">Todos</option>
                        <option value="1">Tesorería</option>
                        <option value="2">Jef. Finanzas</option>
                        <option value="3">Ger. Administración</option>
                        <option value="4">Ger. General</option>
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={handleSearchClick} isLoading={isLoading} />

            {/* Resumen */}
            <div style={{
                marginTop: '15px',
                padding: '10px 15px',
                borderRadius: '8px',
                backgroundColor: '#f8f8f8',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '24px',
                flexWrap: 'wrap',
                width: 'fit-content'
            }}>
                <p style={{ color: 'green', fontSize: '1em', fontWeight: 'bold', margin: 0 }}>
                    N° de registros: {pagos.length}
                </p>
                <p style={{ color: '#333', fontSize: '1em', fontWeight: 'normal', margin: 0 }}>
                    Total: <strong>S/ {totalMonto.toFixed(2)}</strong>
                </p>
                <p style={{ color: '#721c24', fontSize: '1em', fontWeight: 'normal', margin: 0 }}>
                    Pendiente: <strong>S/ {totalPendiente.toFixed(2)}</strong>
                </p>
                <p style={{ color: '#155724', fontSize: '1em', fontWeight: 'normal', margin: 0 }}>
                    Pagado: <strong>S/ {totalPagado.toFixed(2)}</strong>
                </p>
            </div>

            {/* Botón nuevo */}
            <div className="nuevo-movimiento-section">
                <ButtonInsert onClick={handleCreateClick} />
            </div>

            {/* Tabla */}
            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>N° PAGO</th>
                            <th>PROVEEDOR</th>
                            <th>FECHA SOLICITUD</th>
                            <th>COMPROBANTE</th>
                            <th>SERIE / NÚM.</th>
                            <th>TIPO</th>
                            <th>MONTO TOTAL</th>
                            <th>NIVEL APROBACIÓN</th>
                            <th>PLAZO</th>
                            <th>ESTADO PROCESO</th>
                            <th>ESTADO PAGO</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagos.length > 0 ? (
                            pagos.map(p => (
                                <tr key={p.pago_id}>
                                    <td>{p.numero_pago ?? '—'}</td>
                                    <td>{p.proveedor ?? '—'}</td>
                                    <td>{formatDate(p.fecha_solicitud)}</td>
                                    <td>{LABEL_TIPO_COMPROBANTE[p.tipo_comprobante] ?? '—'}</td>
                                    <td>{[p.serie_comprobante, p.numero_comprobante].filter(Boolean).join(' / ') || '—'}</td>
                                    <td>{LABEL_TIPO_BIEN_SERVICIO[p.tipo_bien_servicio] ?? '—'}</td>
                                    <td>{formatMonto(p.monto_total)}</td>
                                    <td>{LABEL_NIVEL_APROBACION[p.nivel_aprobacion] ?? '—'}</td>
                                    <td>{p.plazo_dias ? `${p.plazo_dias === 4 ? 'A convenir' : `${p.plazo_dias} día(s)`}` : '—'}</td>
                                    <td>
                                        <span style={{
                                            padding: '3px 10px',
                                            borderRadius: '12px',
                                            fontSize: '0.82em',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            ...COLOR_ESTADO_PROCESO[p.estado_proceso]
                                        }}>
                                            {LABEL_ESTADO_PROCESO[p.estado_proceso] ?? '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <span style={{
                                            padding: '3px 10px',
                                            borderRadius: '12px',
                                            fontSize: '0.82em',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            ...COLOR_ESTADO_PAGO[p.estado_pago]
                                        }}>
                                            {LABEL_ESTADO_PAGO[p.estado_pago] ?? '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="acciones-container">
                                            <ButtonUpdate onClick={() => handleEditClick(p)} />
                                            <ButtonDelete onClick={() => handleDeleteClick(p.pago_id)} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={12} className="text-center text-gray-500 py-4">
                                    No hay pagos registrados para el período seleccionado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalFinanzasPago
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                pago={selectedPago}
                onSave={handleSave}
            />
        </div>
    );
};

export default FinanzasPago;